import React, { useEffect } from 'react'
import { useDeleteWellData } from '../../../../hooks/use-api'
import { QueryObserverResult, RefetchOptions } from 'react-query'
import { Button, createToastMessage, Modal, ToastMessage } from '@msaf/core-react'

export type ConfirmDeleteProps = {
  requestUrl?: string
  isShowModal?: boolean
  contentLabel?: string
  heading?: string
  bodyText?: string
  successMessage?: string
  errorMessage?: string
  refetch?: (options?: RefetchOptions | undefined) => Promise<QueryObserverResult<any, unknown>>
  postDeleteAction?: VoidFunction
  onModalClose: VoidFunction
}

export default function ConfirmDelete({
  requestUrl,
  isShowModal = false,
  contentLabel = 'Action confirmation dialog',
  heading = 'Delete record',
  bodyText = 'This will delete the record. Are you sure you wish to proceed?',
  successMessage = 'Record deleted',
  errorMessage = 'Could not delete the record. Please try again.',
  postDeleteAction,
  onModalClose,
  children,
}: React.PropsWithChildren<ConfirmDeleteProps>) {
  const { mutate, isError, isSuccess, error } = useDeleteWellData()

  useEffect(() => {
    if (isError && error) {
      createToastMessage(<ToastMessage messageType='error' title='Error' message={errorMessage} />)
    }
  }, [isError, error, errorMessage])

  useEffect(() => {
    if (isSuccess) {
      createToastMessage(<ToastMessage messageType='success' title='Success' message={successMessage} />)
      postDeleteAction && postDeleteAction()
    }
    // Excluded 'postDeleteAction' from dependency list to prevent
    // triggering multiple success messages
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, successMessage])

  const handleDelete = () => {
    if (requestUrl) {
      mutate(requestUrl)
    }
  }

  return (
    <>
      <Modal
        isOpen={isShowModal}
        contentLabel={contentLabel}
        onRequestClose={onModalClose}
        heading={heading}
        body={bodyText}
        buttons={
          <>
            <Button buttonStyle='primary' label='Delete' onClick={handleDelete} />
            <Button buttonStyle='text-action' label='Cancel' onClick={onModalClose} />
          </>
        }
      />
      {children}
    </>
  )
}
